import { ReactNode, useMemo, useState } from "react";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";

import { CircleAlertGrayIcon, CircleAlertOrangeIcon, DownloadIcon, InfoIcon } from "../../../../assets/icons";
import { CardFrame } from "../../../../common/components/card-frame/CardFrame";
import CommonDatePicker from "../../../../common/components/date-picker/CommonDatePicker";
import { FailedToLoadData } from "../../../../common/components/failed-to-load-data/FailedToLoadData";
import Badge, { BadgeVariant } from "../../../../common/components/xepp/badge/Badge";
import Button from "../../../../common/components/xepp/button/Button";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import Tabs from "../../../../common/components/xepp/tab/Tabs";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import { ExceptionLine, parseReconcilliationResult } from "../../../../model/reconcilliation/reconcilliation";
import {
	useGetReconcilliationHistoryDataQuery,
	useLazyDownloadReconcilliationReportQuery,
} from "../../../../service/reconcilliation-service";
import { getErrorMessage } from "../../../../utils/error-handler";
import { SummaryCard } from "../dashboard/components/summary-card/SummaryCard";
import { TransactionsDetails } from "./components/transactions-details/TransactionsDetails";
import styles from "./Reconcilliation.module.scss";

/**
 * The job reconcilles a day only once it has ended, so yesterday is both the default and the
 * newest selectable day. Normalised to midnight so the calendar compares whole days — with a
 * time of day attached, "today" can still slip through as selectable.
 */
const yesterday = (): Date => {
	const date = new Date();
	date.setDate(date.getDate() - 1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const toDateParam = (date: Date): string => {
	const pad = (value: number) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const SUMMARY_TAB = "summary";
const DETAILS_TAB = "details";

const formatPickerLabel = (date: Date): string =>
	date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });

export const Reconcilliation = () => {
	useConfigLayout({
		showBreadCrumb: true,
		showCountrySelector: false,
		showExportButton: false,
		pageTitle: "Reconciliation Job",
	});

	const [date, setDate] = useState<Date>(yesterday());
	const [activeTab, setActiveTab] = useState(SUMMARY_TAB);

	const { data, isFetching, isError } = useGetReconcilliationHistoryDataQuery(toDateParam(date));
	const [downloadReport, { isFetching: isDownloading }] = useLazyDownloadReconcilliationReportQuery();

	// The report is served as a short-lived blob url, so the tab is opened on the url the Pdf
	// service hands back rather than on a response body.
	const handleDownload = () => {
		if (!data) return;

		downloadReport(data.id)
			.unwrap()
			.then(url => {
				if (url) window.open(url, "_blank");
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const result = useMemo(() => (data ? parseReconcilliationResult(data) : null), [data]);

	const renderRow = (label: string, value: string, isTotal = false) => (
		<div className={`${styles.row} ${isTotal ? styles.totalRow : ""}`} key={label}>
			<span className={styles.rowLabel}>{label}</span>
			<span className={styles.rowValue}>{value}</span>
		</div>
	);

	/**
	 * The vending timeline draws its completed state as a filled circle with a check glyph rather
	 * than an icon asset, so the cleared rows repeat that treatment at the size of the alert icons.
	 */
	const clearedIcon = <span className={`${styles.exceptionIcon} ${styles.clearedIcon}`}>✓</span>;

	const renderExceptionRow = (
		label: string,
		line: ExceptionLine,
		icon: ReactNode,
		badgeVariant: BadgeVariant,
		badgeText: string
	) => (
		<div className={styles.exceptionRow} key={label}>
			<span className={styles.exceptionLabel}>
				{icon}
				{label}
			</span>
			<span className={styles.exceptionValue}>
				{line.amount.toLocaleString()}
				<Badge variant={badgeVariant}>{badgeText}</Badge>
			</span>
		</div>
	);

	const renderSummary = () => {
		if (!result) return null;

		const { changes, exceptions } = result;

		return (
			<div className={styles.summary}>
				<div className={styles.cards}>
					<SummaryCard
						title="Settled Today"
						value={result.settledToday.toLocaleString()}
						percentageChange={changes.settledToday}
					/>
					<SummaryCard
						title="Delivered Today"
						value={result.deliveredToday.toLocaleString()}
						percentageChange={changes.deliveredToday}
					/>
					<SummaryCard
						title="Owed (Backlog)"
						value={result.closingOwedMeasured.toLocaleString()}
						percentageChange={changes.owedBacklog}
					/>
					<SummaryCard
						title="Variance"
						value={result.variance.toLocaleString()}
						percentageChange={changes.variance}
					/>
				</div>

				<div className={styles.panels}>
					<CardFrame>
						<div className={styles.panelBody}>
							<h4 className={styles.panelTitle}>Obligation Ledger</h4>
							<div className={styles.rows}>
								{renderRow("Opening Owed (Brought Forward)", result.openingOwed.toLocaleString())}
								{renderRow("Settled Today", result.settledToday.toLocaleString())}
								{renderRow("Delivered Today", result.deliveredToday.toLocaleString())}
								{renderRow("Closing Owed (Calculated)", result.closingOwedCalculated.toLocaleString())}
								{renderRow("Closing Owed (Measured Directly)", result.closingOwedMeasured.toLocaleString())}
								{renderRow("Variance", result.variance.toLocaleString(), true)}
							</div>
						</div>
					</CardFrame>

					<CardFrame>
						<div className={styles.panelBody}>
							<h4 className={styles.panelTitle}>Backlog Aging</h4>
							<div className={styles.rows}>
								{renderRow("0-1 day (In Retry Queue)", result.backlogAging.zeroToOneDay.toLocaleString())}
								{renderRow("2-3 Days", result.backlogAging.twoToThreeDays.toLocaleString())}
								{renderRow("4+ Days (Stuck)", result.backlogAging.fourPlusDays.toLocaleString())}
								{/* The buckets are counted on the measured set, so that is what they sum to. */}
								{renderRow("Closing Owed (Measured Directly)", result.closingOwedMeasured.toLocaleString(), true)}
								<p className={`${styles.note} ${styles.dividedNote}`}>
									<InfoIcon className={styles.noteIcon} />
									Backlog over 3 days signals a vend that is genuinely stuck rather than simply retrying.
								</p>
							</div>
						</div>
					</CardFrame>
				</div>

				<CardFrame>
					<h4 className={styles.panelTitle}>Exceptions &amp; Flags</h4>
					<div className={styles.rows}>
						{renderExceptionRow(
							"Payment Hub False Positives",
							exceptions.paymentHubFalsePositives,
							exceptions.paymentHubFalsePositives.count === 0 ? (
								clearedIcon
							) : (
								<CircleAlertOrangeIcon className={styles.exceptionIcon} />
							),
							exceptions.paymentHubFalsePositives.count === 0 ? "active" : "failed",
							exceptions.paymentHubFalsePositives.count === 0
								? "Clear"
								: `${exceptions.paymentHubFalsePositives.count} Transactions`
						)}
						{renderExceptionRow(
							"Double Vends",
							exceptions.doubleVends,
							exceptions.doubleVends.count === 0 ? clearedIcon : <CircleAlertOrangeIcon className={styles.exceptionIcon} />,
							exceptions.doubleVends.count === 0 ? "active" : "failed",
							exceptions.doubleVends.count === 0 ? "Clear" : `${exceptions.doubleVends.count} Transactions`
						)}
						{renderExceptionRow(
							"Refunded After Delivery",
							exceptions.refundedAfterDelivery,
							<CircleAlertOrangeIcon className={styles.exceptionIcon} />,
							"pending",
							`${exceptions.refundedAfterDelivery.count} Transactions - Known Cost`
						)}
						{renderExceptionRow(
							"Late Completions (Informational)",
							exceptions.lateCompletions,
							<CircleAlertGrayIcon className={styles.exceptionIcon} />,
							"inactive",
							`${exceptions.lateCompletions.count} Transactions - Avg ${exceptions.lateCompletions.averageDays.toFixed(1)} Days`
						)}
						<p className={styles.note}>
							<InfoIcon className={styles.noteIcon} />
							Only non-zero leakage lines require action. Refunds after delivery are an intentional, tracked cost. Late
							completions self-balance and are shown for transparency.
						</p>
					</div>
				</CardFrame>
			</div>
		);
	};

	// The wrapper keeps the picker a chip — its own container is flex:1 and would otherwise
	// stretch across the whole tab row. The download covers the summary only, so it is hidden
	// while the details tab is open.
	const headerActions = (
		<div className={styles.headerActions}>
			{activeTab === SUMMARY_TAB && (
				<Button
					text="Download"
					variant="outline"
					leftIcon={<DownloadIcon className={styles.downloadIcon} />}
					onClick={handleDownload}
					disabled={!data || isFetching || isDownloading}
				/>
			)}
			<div className={styles.datePicker}>
				<CommonDatePicker
					initialDate={date}
					onChange={setDate}
					maxDate={yesterday()}
					formatValue={formatPickerLabel}
				/>
			</div>
		</div>
	);

	/**
	 * Both tabs read the same record, so loading, failure and "job has not run" resolve to one
	 * placeholder shown inside whichever tab is open — the tab bar and date picker stay put so
	 * another date can be picked without leaving the page.
	 */
	const placeholder = () => {
		if (isFetching)
			return (
				<div className={styles.centered} data-testid="reconcilliation-loader">
					<ClipLoader />
				</div>
			);

		if (isError) return <FailedToLoadData text="Failed to load reconcilliation data. Please try again." />;

		return (
			<EmptyState
				title="No reconcilliation data"
				description="The reconcilliation job has not produced a record for this date yet."
			/>
		);
	};

	const hasReport = !isFetching && !isError && result !== null;

	return (
		<div className={styles.page}>
			<Tabs
				headerRightContent={headerActions}
				onTabChange={setActiveTab}
				tabs={[
					{ key: SUMMARY_TAB, label: "Summary", content: hasReport ? renderSummary() : placeholder() },
					{
						key: DETAILS_TAB,
						label: "Transactions Details",
						content: hasReport ? <TransactionsDetails result={result!} /> : placeholder(),
					},
				]}
			/>
		</div>
	);
};
